import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Tooltip } from "primereact/tooltip";
import EzControlledDropDowntWithFloatingLabel from "../../../../common/EzForm/EzControlledDropDown/WithFloatingLabel";
import EzControlledCalendarWithFloatingLabel from "../../../../common/EzForm/EzControlledCalendar/WithFloatingLabel";
import EzControlledCheckBoxWithLabel from "../../../../common/EzForm/EzControlledCheckBox/WithLabel";
import EzControlledMultiSelecttWithFloatingLabel from "../../../../common/EzForm/EzControlledMultiSelect/WithFloatingLabel";
import "./styles.css";
import Breadcrumb from "../../../../common/Breadcrumb";
import { useStyles } from "../style";
import {
  RateAdminAdvanceRateBreadcrumb,
  rateGroupDD,
  networksDD,
  rateOptionDD,
  rateTypeDD,
  DAY_INDICES,
  WEEK_LABELS,
  LOR_VALUES,
} from "./constant";
import { Container, Grid } from "@mui/material";
import { EzToast, TOAST_SEVERITY } from "../../../../common/EzToast";
import { useNavigate } from "react-router-dom";
import { APP_URLS } from "../../../../utils/constant";
import { formatDatesHyphan } from "../../../../utils";
import { additionbaselor } from "../../../../utils/common";
import { formatBlackoutContent, parseChangedFieldIds } from "../../../../utils/rate";

// Helper function to sanitize field names for CSS selectors (remove spaces and special chars)
const sanitizeForCss = (str) => {
  return str.replace(/[^a-zA-Z0-9-_]/g, '-').replace(/-+/g, '-');
};

// BlackoutTooltip wrapper component to show blackout message on hover
const BlackoutTooltip = ({ children, fieldName, isBlackout, blackoutDates, rateId }) => {
  if (!isBlackout) {
    return children;
  }

  const tooltipId = `blackout-tooltip-adv-${rateId || 'default'}-${sanitizeForCss(fieldName)}`;
  const hasBlackoutDates = blackoutDates && blackoutDates.length > 0;

  return (
    <div className="blackout-tooltip-wrapper">
      <Tooltip 
        target={`.${tooltipId}`} 
        position="top" 
        className="blackout-tooltip"
        autoHide={false}
      >
        <div className="blackout-tooltip-content">
          <strong style={{ color: '#ff6b6b' }}>Blackout applied on this</strong>
          {hasBlackoutDates && (
            <div style={{ marginTop: '8px' }} dangerouslySetInnerHTML={{ __html: formatBlackoutContent(blackoutDates) }} />
          )}
        </div>
      </Tooltip>
      <div className={tooltipId}>
        {children}
      </div>
    </div>
  );
};

// Priority: BlackoutTooltip > RateMirrorTooltip
// RateMirrorTooltip wrapper component to show rate mirror source info on hover
const RateMirrorTooltip = ({ children, fieldName, isRateMirror, rateMirrorFrom, isBlackout, rateId }) => {
  // Skip if blacked out (BlackoutTooltip takes priority) OR if not mirrored
  if (isBlackout || !isRateMirror || !rateMirrorFrom) {
    return children;
  }

  const tooltipId = `rate-mirror-tooltip-adv-${rateId || 'default'}-${sanitizeForCss(fieldName)}`;

  const formatRateMirrorContent = () => {
    return `<div style="padding: 10px; min-width: 260px;"><div style="font-weight: 600; margin-bottom: 8px; border-bottom: 2px solid #ed6c02; padding-bottom: 6px; color: #ed6c02;"><i class="pi pi-link" style="margin-right: 6px;"></i>Mirrored From Rate #${rateMirrorFrom.id}</div><div style="display: flex; justify-content: space-between; margin-bottom: 5px;"><span style="color: #666;">Network:</span><span style="font-weight: 500;">${rateMirrorFrom.fkNetworkFrom || 'N/A'}</span></div><div style="display: flex; justify-content: space-between; margin-bottom: 5px;"><span style="color: #666;">Location:</span><span style="font-weight: 500; font-size: 11px;">${rateMirrorFrom.fkLocationFrom || 'N/A'}</span></div><div style="display: flex; justify-content: space-between; margin-bottom: 5px;"><span style="color: #666;">Rate Code:</span><span style="font-weight: 500;">${rateMirrorFrom.fkRateGroupFrom || 'N/A'}</span></div><div style="display: flex; justify-content: space-between; margin-bottom: 5px;"><span style="color: #666;">Vehicle Class:</span><span style="font-weight: 500;">${rateMirrorFrom.fkVehicleClassFrom || 'N/A'}</span></div><div style="display: flex; justify-content: space-between;"><span style="color: #666;">Rate Type:</span><span style="font-weight: 500;">${rateMirrorFrom.fkRateTypeIdFrom || 'N/A'}</span></div></div>`;
  };

  return (
    <div className="rate-mirror-tooltip-wrapper">
      <Tooltip 
        target={`.${tooltipId}`} 
        position="top" 
        className="rate-mirror-tooltip"
        autoHide={false}
      >
        <div className="rate-mirror-tooltip-content" dangerouslySetInnerHTML={{ __html: formatRateMirrorContent() }} />
      </Tooltip>
      <div className={tooltipId} style={{ cursor: 'help' }}>
        {children}
      </div>
    </div>
  );
};

// Priority: BlackoutTooltip > RateMirrorTooltip > RevenuePreviewTooltip
// RevenuePreviewTooltip wrapper component to show revenue preview on hover for LOR fields
const RevenuePreviewTooltip = ({ children, lorId, revenuePreviewData, isLoading, rateId, isBlackout, isRateMirror }) => {
  // Don't show revenue preview tooltip if field is blacked out OR if rate is mirrored
  if (isBlackout || isRateMirror) {
    return children;
  }

  // Get the lorBreakdowns array
  const lorBreakdowns = revenuePreviewData?.lorBreakdowns || 
    (Array.isArray(revenuePreviewData) ? revenuePreviewData : null);
  
  if (!lorBreakdowns || isLoading) {
    return children;
  }

  // Match by lorId or fkLorId for compatibility
  const lorData = lorBreakdowns.find(item => 
    item.lorId === lorId || item.fkLorId === lorId
  );
  if (!lorData) {
    return children;
  }

  const tooltipId = `revenue-preview-tooltip-adv-${rateId || 'default'}-${lorId}`;
  
  const formatTooltipContent = (data) => {
    if (!data) return '';
    
    const exc = data.exclusive || {};
    
    let html = '<div style="padding: 4px; min-width: 220px;">';
    
    if (data.duration || data.ratePlan) {
      html += `<div style="font-weight: 600; margin-bottom: 4px; border-bottom: 1px solid #ddd; padding-bottom: 2px;">${data.duration || ''} ${data.ratePlan ? `(${data.ratePlan})` : ''}</div>`;
    }
    
    if (exc.fees !== undefined) {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 2px;"><span>Fees:</span><span>$${exc.fees} CAD</span></div>`;
    }
    if (exc.underage_driver_fee !== undefined && parseFloat(exc.underage_driver_fee) > 0) {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 2px;"><span>Underage Fee:</span><span>$${exc.underage_driver_fee} CAD</span></div>`;
    }
    if (exc.airport_facility_charge !== undefined && parseFloat(exc.airport_facility_charge) > 0) {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 2px;"><span>Airport Charge:</span><span>$${exc.airport_facility_charge} CAD</span></div>`;
    }
    if (exc.airport_concession_fee !== undefined && parseFloat(exc.airport_concession_fee) > 0) {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 2px;"><span>Concession Fee:</span><span>$${exc.airport_concession_fee} CAD</span></div>`;
    }
    if (exc.after_hour_fee !== undefined && parseFloat(exc.after_hour_fee) > 0) {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 2px;"><span>After Hour Fee:</span><span>$${exc.after_hour_fee} CAD</span></div>`;
    }
    if (exc.taxes !== undefined) {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 2px;"><span>Taxes:</span><span>$${exc.taxes} CAD</span></div>`;
    }
    
    if (exc.daily_rate !== undefined) {
      html += `<div style="display: flex; justify-content: space-between; margin-bottom: 2px; font-weight: 600; margin-top: 4px; border-top: 1px solid #ddd; padding-top: 2px;"><span>Daily Rate:</span><span>$${exc.daily_rate}</span></div>`;
    }
    
    if (exc.payNowRate) {
      html += `<div style="margin-top: 2px; padding-top: 2px; border-top: 1px dashed #ccc;"><div style="font-weight: 600; color: #2e7d32; margin-bottom: 2px;">Pay Now Rate</div><div style="display: flex; justify-content: space-between; margin-bottom: 2px;"><span>Daily Rate:</span><span>$${exc.payNowRate.daily_rate || '0.00'}</span></div><div style="display: flex; justify-content: space-between;"><span>Net Rate:</span><span>$${exc.payNowRate.net_rate || '0.00'}</span></div></div>`;
    }
    
    html += '</div>';
    return html;
  };

  return (
    <div className="revenue-preview-tooltip-wrapper">
      <Tooltip 
        target={`.${tooltipId}`} 
        position="top" 
        className="revenue-preview-tooltip"
        autoHide={false}
      >
        <div className="revenue-preview-tooltip-content" dangerouslySetInnerHTML={{ __html: formatTooltipContent(lorData) }} />
      </Tooltip>
      <div className={tooltipId} style={{ cursor: revenuePreviewData ? 'help' : 'default' }}>
        {children}
      </div>
    </div>
  );
};

const CreateRateAdminAdvanceRateComponent = (props) => {
  const navigate = useNavigate();
  const {
    initCreateRateAdminAdvanceRateScreen,
    createRateAdminAdvanceRate,
    editRateAdminAdvanceRate,
    updateAdvanceRate,
    searchAdvanceRate,
    isLoading,
    isRateAdminAdvanceRateCreateSuccess,
    isRateAdminAdvanceRateEditSuccess,
    isRateAdminAdvanceRateDetailSuccess,
    isUpdateAdvanceRateSuccess,
    isUpdateAdvanceRateError,
    isEdit,
    isUpdate,
    formFieldValueMap,
    locationDD,
    vehicleCategoryDD,
    updateData,
    searchData,
    isSearchLoading,
    isSearchSuccess,
    isSearchError,
    // Category by location
    fetchCategoryByLocation,
    locationCategoryDD,
    isLocationCategoryDDLoading,
    screenPermissions = {},
    // Revenue preview
    fetchRevenuePreview,
    revenuePreviewData,
    isRevenuePreviewSuccess,
    // Dropdowns from API
    RateNetworkDD,
    RateCodeDD,
    RateOptionsDD,
  } = props;

  const classes = useStyles();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: useMemo(() => {
      return formFieldValueMap;
    }, [formFieldValueMap]),
  });

  // Local state for excludeWeekends checkbox
  const [excludeWeekendsState, setExcludeWeekendsState] = useState(true);

  // Track if search has been performed in Update mode
  const [hasSearched, setHasSearched] = useState(false);

  // Watch selected categories
  const selectedCategories = watch("fkCategory");

  // Watch location for fetching location-specific categories
  const selectedLocationId = watch("fkLocationId");

  // Computed category options - use location-filtered categories when available
  const categoryOptions = locationCategoryDD?.length > 0 ? locationCategoryDD : vehicleCategoryDD;

  // Store all rate data by category (to preserve values when toggling)
  const [allWeekdaysData, setAllWeekdaysData] = useState({});
  const [allWeekendData, setAllWeekendData] = useState({});
  const [allMonthlyData, setAllMonthlyData] = useState({});

  // Create initial data structure for a single category - Weekdays
  const createWeekdaysCategoryData = (categoryId, categoryName) => {
    const row = { id: categoryId, category: categoryName };
    LOR_VALUES.forEach(lor => {
      row[`lor_${lor}`] = '';
    });
    return row;
  };

  // Create initial data structure for a single category - Weekend
  const createWeekendCategoryData = (categoryId, categoryName) => {
    const row = { id: categoryId, category: categoryName };
    LOR_VALUES.forEach(lor => {
      row[`lor_${lor}`] = '';
    });
    return row;
  };

  // Create initial data structure for a single category - Monthly
  const createMonthlyCategoryData = (categoryId, categoryName) => {
    const row = { id: categoryId, category: categoryName };
    row['lor_Base'] = '';
    row['lor_Extra Day'] = '';
    return row;
  };

  // Get filtered weekdays data based on selected categories
  const weekdaysData = useMemo(() => {
    const searchValues = Object.values(allWeekdaysData);
    if (isUpdate && searchValues.length > 0) {
      return searchValues;
    }

    if (!selectedCategories || selectedCategories.length === 0) {
      return [];
    }
    
    return selectedCategories.map(categoryId => {
      const category = categoryOptions?.find(c => c.id === categoryId);
      let categoryName = category?.name || String(categoryId);
      
      // Extract value inside brackets if present (e.g., "Compact Car (CCAR)" -> "CCAR")
      const bracketMatch = String(categoryName).match(/\(([^)]+)\)/);
      if (bracketMatch) {
        categoryName = bracketMatch[1];
      }
      
      // Return existing data if available, otherwise create new
      if (allWeekdaysData[categoryId]) {
        return { ...allWeekdaysData[categoryId], category: categoryName };
      }
      return createWeekdaysCategoryData(categoryId, categoryName);
    });
  }, [selectedCategories, allWeekdaysData, categoryOptions, isUpdate]);

  // Get filtered weekend data based on selected categories
  const weekendData = useMemo(() => {
    const searchValues = Object.values(allWeekendData);
    if (isUpdate && searchValues.length > 0) {
      return searchValues;
    }

    if (!selectedCategories || selectedCategories.length === 0) {
      return [];
    }
    
    return selectedCategories.map(categoryId => {
      const category = categoryOptions?.find(c => c.id === categoryId);
      let categoryName = category?.name || String(categoryId);
      
      // Extract value inside brackets if present (e.g., "Compact Car (CCAR)" -> "CCAR")
      const bracketMatch = String(categoryName).match(/\(([^)]+)\)/);
      if (bracketMatch) {
        categoryName = bracketMatch[1];
      }
      
      // Return existing data if available, otherwise create new
      if (allWeekendData[categoryId]) {
        return { ...allWeekendData[categoryId], category: categoryName };
      }
      return createWeekendCategoryData(categoryId, categoryName);
    });
  }, [selectedCategories, allWeekendData, categoryOptions, isUpdate]);

  // Get filtered monthly data based on selected categories
  const monthlyData = useMemo(() => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return [];
    }
    
    return selectedCategories.map(categoryId => {
      const category = categoryOptions?.find(c => c.id === categoryId);
      let categoryName = category?.name || String(categoryId);
      
      // Extract value inside brackets if present (e.g., "Compact Car (CCAR)" -> "CCAR")
      const bracketMatch = String(categoryName).match(/\(([^)]+)\)/);
      if (bracketMatch) {
        categoryName = bracketMatch[1];
      }
      
      // Return existing data if available, otherwise create new
      if (allMonthlyData[categoryId]) {
        return { ...allMonthlyData[categoryId], category: categoryName };
      }
      return createMonthlyCategoryData(categoryId, categoryName);
    });
  }, [selectedCategories, allMonthlyData, categoryOptions]);

  // Update weekdays data handler
  const updateWeekdaysData = (categoryId, lor, value) => {
    setAllWeekdaysData(prev => {
      const category = categoryOptions?.find(c => c.id === categoryId);
      const categoryName = category?.name || categoryId;
      const existingData = prev[categoryId] || createWeekdaysCategoryData(categoryId, categoryName);
      return {
        ...prev,
        [categoryId]: {
          ...existingData,
          [`lor_${lor}`]: value
        }
      };
    });
  };

  // Update weekend data handler
  const updateWeekendData = (categoryId, lor, value) => {
    setAllWeekendData(prev => {
      const category = categoryOptions?.find(c => c.id === categoryId);
      const categoryName = category?.name || categoryId;
      const existingData = prev[categoryId] || createWeekendCategoryData(categoryId, categoryName);
      return {
        ...prev,
        [categoryId]: {
          ...existingData,
          [`lor_${lor}`]: value
        }
      };
    });
  };

  // Update monthly data handler
  const updateMonthlyData = (categoryId, lor, value) => {
    setAllMonthlyData(prev => {
      const category = categoryOptions?.find(c => c.id === categoryId);
      const categoryName = category?.name || categoryId;
      const existingData = prev[categoryId] || createMonthlyCategoryData(categoryId, categoryName);
      return {
        ...prev,
        [categoryId]: {
          ...existingData,
          [`lor_${lor}`]: value
        }
      };
    });
  };

  const navigateToRateAdminAdvanceRateScreen = () => {
    navigate(APP_URLS.RATEADMIN_ADVANCERATE.UPDATE);
  };

  const handleCreateRateAdminAdvanceRate = () => {
    navigate(APP_URLS.RATEADMIN_ADVANCERATE.CREATE);
  };

  useEffect(() => {
    if (isRateAdminAdvanceRateCreateSuccess || isRateAdminAdvanceRateEditSuccess) {
      // Find defaults from API dropdowns
      const defaultNetwork = RateNetworkDD?.find(item => item.isDefault === 1);
      const defaultNetworkId = defaultNetwork?.id || RateNetworkDD?.[0]?.id;
      
      const defaultRateGroup = RateCodeDD?.find(item => item.isDefault === 1);
      const defaultRateGroupId = defaultRateGroup?.id || RateCodeDD?.[0]?.id;
      
      const actualRateOption = RateOptionsDD?.find(item => item.name === "Actual Rate");
      const defaultRateOptionId = actualRateOption?.id || RateOptionsDD?.[0]?.id;
      
      const exclusiveType = rateTypeDD?.find(item => item.name === "Exclusive");
      const defaultRateTypeId = exclusiveType?.id || rateTypeDD?.[0]?.id;
      
      // Reset form but keep fixed dropdown values
      reset({
        fkRateGroup: defaultRateGroupId,
        fkNetworkId: defaultNetworkId,
        rateOption: defaultRateOptionId,
        fkRateTypeId: defaultRateTypeId,
        excludeWeekends: false,
      });
      // Clear table data
      setAllWeekdaysData({});
      setAllWeekendData({});
      setAllMonthlyData({});
      setExcludeWeekendsState(false);
    }
  }, [isRateAdminAdvanceRateCreateSuccess, isRateAdminAdvanceRateEditSuccess]);

  useEffect(() => {
    initCreateRateAdminAdvanceRateScreen();
    // Set default fkRateTypeId by finding "Exclusive" from rateTypeDD
    const exclusiveType = rateTypeDD?.find(item => item.name === "Exclusive");
    setValue("fkRateTypeId", exclusiveType?.id || rateTypeDD?.[0]?.id);
    setValue("excludeWeekends", false); // Default to show both tables
  }, []);

  useEffect(() => {
    if (RateNetworkDD?.length > 0) {
      // Find default network (isDefault: 1) or use first available
      const defaultNetwork = RateNetworkDD.find(item => item.isDefault === 1);
      const defaultNetworkId = defaultNetwork?.id || RateNetworkDD[0]?.id;
      if (defaultNetworkId) {
        setValue("fkNetworkId", defaultNetworkId);
      }
    }
  }, [RateNetworkDD, setValue]);

  useEffect(() => {
    if (RateCodeDD?.length > 0) {
      // Find default rate group (isDefault: 1) or use first available
      const defaultRateGroup = RateCodeDD.find(item => item.isDefault === 1);
      const defaultRateGroupId = defaultRateGroup?.id || RateCodeDD[0]?.id;
      if (defaultRateGroupId) {
        setValue("fkRateGroup", defaultRateGroupId);
      }
    }
  }, [RateCodeDD, setValue]);

  useEffect(() => {
    if (RateOptionsDD?.length > 0) {
      // Find "Actual Rate" option or use first available
      const actualRateOption = RateOptionsDD.find(item => item.name === "Actual Rate");
      const defaultRateOption = actualRateOption?.id || RateOptionsDD[0]?.id;
      if (defaultRateOption) {
        setValue("rateOption", defaultRateOption);
      }
    }
  }, [RateOptionsDD, setValue]);

  // Fetch categories when location changes - for all modes (create, edit, update, view)
  useEffect(() => {
    if (selectedLocationId && fetchCategoryByLocation) {
      fetchCategoryByLocation({ locationId: selectedLocationId });
      // Clear category selection and table data whenever location changes
      setValue("fkCategory", []);
      setAllWeekdaysData({});
      setAllWeekendData({});
      setAllMonthlyData({});
    }
  }, [selectedLocationId, fetchCategoryByLocation, setValue]);

  useEffect(() => {
    if (isRateAdminAdvanceRateDetailSuccess) {
      reset({
        ...formFieldValueMap,
      });
    }
  }, [isRateAdminAdvanceRateDetailSuccess, formFieldValueMap]);

  // Populate form data when update data is available
  useEffect(() => {
    if (isUpdate && updateData) {
      const { data, rateMappings } = updateData;
      
      // Get unique location IDs from rateMappings
      const locationIds = [...new Set(rateMappings.map(m => m.locationId))];
      
      // Get unique category IDs from rateMappings
      const categoryIds = [...new Set(rateMappings.map(m => m.categoryId))];
      
      // Set form values - location is single select, take first one
      setValue("fkLocationId", locationIds[0]);
      setValue("fkCategory", categoryIds);
      setValue("startDate", data.startDate ? new Date(data.startDate) : null);
      setValue("endDate", data.endDate ? new Date(data.endDate) : null);
      
      // Build weekdays and weekend data from categories
      const newWeekdaysData = {};
      const newWeekendData = {};
      const newMonthlyData = {};
      
      data.categories?.forEach(cat => {
        const categoryId = cat.fkCategory;
        const category = vehicleCategoryDD?.find(c => c.id === categoryId);
        let categoryName = category?.name || categoryId;
        
        // Extract value inside brackets if present
        const bracketMatch = categoryName?.match?.(/\(([^)]+)\)/);
        if (bracketMatch) {
          categoryName = bracketMatch[1];
        }
        
        // Initialize weekdays row
        const weekdayRow = { id: categoryId, category: categoryName };
        LOR_VALUES.forEach(lor => {
          weekdayRow[`lor_${lor}`] = '';
        });
        
        // Initialize weekend row
        const weekendRow = { id: categoryId, category: categoryName };
        LOR_VALUES.forEach(lor => {
          weekendRow[`lor_${lor}`] = '';
        });
        
        // Populate Weekdays Base from rateBaseDTO (fkRatePlanId: 1 = Daily)
        const dailyBase = cat.rateBaseDTO?.find(r => r.fkRatePlanId === 1);
        if (dailyBase) {
          weekdayRow['lor_Base'] = dailyBase.rate?.toString() || '';
        }
        
        // Populate Weekdays Per Hour from rateExtraHourDTOS (fkRatePlanId: 1)
        const dailyExtraHour = cat.rateExtraHourDTOS?.find(r => r.fkRatePlanId === 1);
        if (dailyExtraHour) {
          weekdayRow['lor_Per Hour'] = dailyExtraHour.rate?.toString() || '';
        }
        
        // Monthly Base (fkRatePlanId: 4) - will be set in monthlyRow
        const monthlyBase = cat.rateBaseDTO?.find(r => r.fkRatePlanId === 4);
        
        // Populate Per Day 1-6 for weekdays from rateLorAdjustmentDTOS (fkLorId 1-6)
        [1, 2, 3, 4, 5, 6].forEach(i => {
          const lorRate = cat.rateLorAdjustmentDTOS?.find(r => r.fkLorId === i);
          if (lorRate) {
            weekdayRow[`lor_Per Day ${i}`] = lorRate.rate?.toString() || '';
          }
        });
        
        // Populate Per Week BASE from rateBaseDTO (fkRatePlanId: 3)
        const weeklyBase = cat.rateBaseDTO?.find(r => r.fkRatePlanId === 3);
        if (weeklyBase) {
          weekdayRow['lor_Per Week BASE'] = weeklyBase.rate?.toString() || '';
        }
        
        // Populate Per Week Extra Days from rateExtraDayDTOS (fkRatePlanId: 3)
        const weeklyExtraDay = cat.rateExtraDayDTOS?.find(r => r.fkRatePlanId === 3);
        if (weeklyExtraDay) {
          weekdayRow['lor_Per Week Extra Days'] = weeklyExtraDay.rate?.toString() || '';
        }
        
        // Populate Per Week 1ST-4TH for weekdays from rateLorAdjustmentDTOS (fkLorId 13-16)
        const weekLabels = ['1ST', '2ND', '3RD', '4TH'];
        weekLabels.forEach((label, idx) => {
          const lorRate = cat.rateLorAdjustmentDTOS?.find(r => r.fkLorId === 13 + idx);
          if (lorRate) {
            weekdayRow[`lor_Per Week ${label}`] = lorRate.rate?.toString() || '';
          }
        });
        
        // Populate Weekend Base from rateBaseDTO (fkRatePlanId: 2 for weekend)
        const weekendBaseRate = cat.rateBaseDTO?.find(r => r.fkRatePlanId === 2);
        if (weekendBaseRate) {
          weekendRow['lor_Base'] = weekendBaseRate.rate?.toString() || '';
        }
        
        // Populate Weekend Per Hour from rateExtraHourDTOS (fkRatePlanId: 2)
        const weekendExtraHour = cat.rateExtraHourDTOS?.find(r => r.fkRatePlanId === 2);
        if (weekendExtraHour) {
          weekendRow['lor_Per Hour'] = weekendExtraHour.rate?.toString() || '';
        }
        
        // Populate Weekend Per Day 1-6 from rateLorAdjustmentDTOS (fkLorId 7-12)
        [1, 2, 3, 4, 5, 6].forEach(i => {
          const lorRate = cat.rateLorAdjustmentDTOS?.find(r => r.fkLorId === 6 + i);
          if (lorRate) {
            weekendRow[`lor_Per Day ${i}`] = lorRate.rate?.toString() || '';
          }
        });
        
        // Initialize monthly row
        const monthlyRow = { id: categoryId, category: categoryName };
        monthlyRow['lor_Base'] = '';
        monthlyRow['lor_Extra Day'] = '';
        
        // Populate Monthly Base from rateBaseDTO (fkRatePlanId: 4)
        if (monthlyBase) {
          monthlyRow['lor_Base'] = monthlyBase.rate?.toString() || '';
        }
        
        // Populate Monthly Extra Day from rateExtraDayDTOS (fkRatePlanId: 4)
        const monthlyExtraDay = cat.rateExtraDayDTOS?.find(r => r.fkRatePlanId === 4);
        if (monthlyExtraDay) {
          monthlyRow['lor_Extra Day'] = monthlyExtraDay.rate?.toString() || '';
        }
        
        newWeekdaysData[categoryId] = weekdayRow;
        newWeekendData[categoryId] = weekendRow;
        newMonthlyData[categoryId] = monthlyRow;
      });
      
      setAllWeekdaysData(newWeekdaysData);
      setAllWeekendData(newWeekendData);
      setAllMonthlyData(newMonthlyData);
    }
  }, [isUpdate, updateData, vehicleCategoryDD, setValue]);

  useEffect(() => {
    if (isUpdate && isSearchSuccess && searchData?.categories) {
      const categories = searchData.categories;
      
      const categoryIds = categories.map(cat => cat.fkCategory);
      

      setValue("fkCategory", categoryIds);

      const newWeekdaysData = {};
      const newWeekendData = {};
      const newMonthlyData = {};
      
      categories.forEach(cat => {
        const categoryId = cat.fkCategory;
        const category = vehicleCategoryDD?.find(c => c.id === categoryId);
        let categoryName = category?.name || String(categoryId);
        
        const bracketMatch = categoryName?.match?.(/\(([^)]+)\)/);
        if (bracketMatch) {
          categoryName = bracketMatch[1];
        }

        const weekdayRow = { 
          id: cat.id,
          fkCategory: categoryId,
          rateId: cat.id,
          category: categoryName,
          startDate: cat.startDate,
          endDate: cat.endDate,
          isRateMirror: cat.isRateMirror,
          rateMirrorFrom: cat.rateMirrorFrom
        };
        LOR_VALUES.forEach(lor => {
          weekdayRow[`lor_${lor}`] = '';
        });

        const weekendRow = { 
          id: cat.id,
          fkCategory: categoryId,
          rateId: cat.id,
          category: categoryName,
          startDate: cat.startDate,
          endDate: cat.endDate,
          isRateMirror: cat.isRateMirror,
          rateMirrorFrom: cat.rateMirrorFrom
        };
        LOR_VALUES.forEach(lor => {
          weekendRow[`lor_${lor}`] = '';
        });
        
        weekdayRow.rateBaseIds = {};
        weekdayRow.rateLorIds = {};
        weekdayRow.rateExtraHourIds = {};
        weekdayRow.rateExtraDayIds = {};
        weekendRow.rateBaseIds = {};
        weekendRow.rateLorIds = {};
        weekendRow.rateExtraHourIds = {};
        weekdayRow.blackoutInfo = {};
        weekendRow.blackoutInfo = {};
        
        weekdayRow.blackoutDates = cat.blackoutDates || [];
        weekendRow.blackoutDates = cat.blackoutDates || [];
        
        const changedIds = parseChangedFieldIds(cat.lastChangeLog);
        weekdayRow.changedFieldIds = changedIds;
        weekendRow.changedFieldIds = changedIds;
        
        const monthlyRow = { 
          id: cat.id,
          fkCategory: categoryId,
          rateId: cat.id,
          category: categoryName,
          startDate: cat.startDate,
          endDate: cat.endDate,
          isRateMirror: cat.isRateMirror,
          rateMirrorFrom: cat.rateMirrorFrom
        };
        monthlyRow['lor_Base'] = '';
        monthlyRow['lor_Extra Day'] = '';
        monthlyRow.rateBaseIds = {};
        monthlyRow.blackoutInfo = {};
        monthlyRow.blackoutDates = cat.blackoutDates || [];
        monthlyRow.changedFieldIds = changedIds;

        const dailyBase = cat.rateBaseDTO?.find(r => r.fkRatePlanId === 1);
        if (dailyBase) {
          weekdayRow['lor_Base'] = dailyBase.rate?.toString() || '';
          weekdayRow.rateBaseIds[1] = dailyBase.id;
          weekdayRow.blackoutInfo['lor_Base'] = dailyBase.isBlackout;
        }
        
        const dailyExtraHour = cat.rateExtraHourDTOS?.find(r => r.fkRatePlanId === 1);
        if (dailyExtraHour) {
          weekdayRow['lor_Per Hour'] = dailyExtraHour.rate?.toString() || '';
          weekdayRow.rateExtraHourIds[1] = dailyExtraHour.id;
          weekdayRow.blackoutInfo['lor_Per Hour'] = dailyExtraHour.isBlackout;
        }
        // Store IDs for plans 3 and 4 so edit payload can UPDATE rather than INSERT
        const extraHour3 = cat.rateExtraHourDTOS?.find(r => r.fkRatePlanId === 3);
        if (extraHour3) weekdayRow.rateExtraHourIds[3] = extraHour3.id;
        const extraHour4 = cat.rateExtraHourDTOS?.find(r => r.fkRatePlanId === 4);
        if (extraHour4) weekdayRow.rateExtraHourIds[4] = extraHour4.id;
        
        const extraDay1 = cat.rateExtraDayDTOS?.find(r => r.fkRatePlanId === 1);
        if (extraDay1) {
          weekdayRow.rateExtraDayIds[1] = extraDay1.id;
        }
        const extraDay2 = cat.rateExtraDayDTOS?.find(r => r.fkRatePlanId === 2);
        if (extraDay2) {
          weekdayRow.rateExtraDayIds[2] = extraDay2.id;
        }
        
        const monthlyBase = cat.rateBaseDTO?.find(r => r.fkRatePlanId === 4);
        if (monthlyBase) {
          monthlyRow['lor_Base'] = monthlyBase.rate?.toString() || '';
          monthlyRow.rateBaseIds[4] = monthlyBase.id;
          monthlyRow.blackoutInfo['lor_Base'] = monthlyBase.isBlackout;
        }
        
        const monthlyExtraDay = cat.rateExtraDayDTOS?.find(r => r.fkRatePlanId === 4);
        if (monthlyExtraDay) {
          monthlyRow['lor_Extra Day'] = monthlyExtraDay.rate?.toString() || '';
          monthlyRow.rateExtraDayIds = monthlyRow.rateExtraDayIds || {};
          monthlyRow.rateExtraDayIds[4] = monthlyExtraDay.id;
          monthlyRow.blackoutInfo['lor_Extra Day'] = monthlyExtraDay.isBlackout;
        }
        
        // Populate Per Day 1-6 for weekdays from rateLorAdjustmentDTOS (fkLorId 1-6)
        [1, 2, 3, 4, 5, 6].forEach(i => {
          const lorRate = cat.rateLorAdjustmentDTOS?.find(r => r.fkLorId === i);
          if (lorRate) {
            weekdayRow[`lor_Per Day ${i}`] = lorRate.rate?.toString() || '';
            weekdayRow.rateLorIds[i] = lorRate.id;
            weekdayRow.blackoutInfo[`lor_Per Day ${i}`] = lorRate.isBlackout;
          }
        });
        
        // Populate Per Week BASE from rateBaseDTO (fkRatePlanId: 3)
        const weeklyBase = cat.rateBaseDTO?.find(r => r.fkRatePlanId === 3);
        if (weeklyBase) {
          weekdayRow['lor_Per Week BASE'] = weeklyBase.rate?.toString() || '';
          weekdayRow.rateBaseIds[3] = weeklyBase.id;
          weekdayRow.blackoutInfo['lor_Per Week BASE'] = weeklyBase.isBlackout;
        }
        
        // Populate Per Week Extra Days from rateExtraDayDTOS (fkRatePlanId: 3)
        const weeklyExtraDay = cat.rateExtraDayDTOS?.find(r => r.fkRatePlanId === 3);
        if (weeklyExtraDay) {
          weekdayRow['lor_Per Week Extra Days'] = weeklyExtraDay.rate?.toString() || '';
          weekdayRow.rateExtraDayIds = weekdayRow.rateExtraDayIds || {};
          weekdayRow.rateExtraDayIds[3] = weeklyExtraDay.id;
          weekdayRow.blackoutInfo['lor_Per Week Extra Days'] = weeklyExtraDay.isBlackout;
        }
        
        // Populate Per Week 1ST-4TH for weekdays from rateLorAdjustmentDTOS (fkLorId 13-16)
        const weekLabels = ['1ST', '2ND', '3RD', '4TH'];
        weekLabels.forEach((label, idx) => {
          const lorRate = cat.rateLorAdjustmentDTOS?.find(r => r.fkLorId === 13 + idx);
          if (lorRate) {
            weekdayRow[`lor_Per Week ${label}`] = lorRate.rate?.toString() || '';
            weekdayRow.rateLorIds[13 + idx] = lorRate.id;
            weekdayRow.blackoutInfo[`lor_Per Week ${label}`] = lorRate.isBlackout;
          }
        });
        
        // Populate Weekend Base from rateBaseDTO (fkRatePlanId: 2 for weekend base)
        const weekendBaseRate = cat.rateBaseDTO?.find(r => r.fkRatePlanId === 2);
        if (weekendBaseRate) {
          weekendRow['lor_Base'] = weekendBaseRate.rate?.toString() || '';
          weekendRow.rateBaseIds[2] = weekendBaseRate.id;
          weekendRow.blackoutInfo['lor_Base'] = weekendBaseRate.isBlackout;
        }
        
        // Populate Weekend Per Hour from rateExtraHourDTOS (fkRatePlanId: 2)
        const weekendExtraHour = cat.rateExtraHourDTOS?.find(r => r.fkRatePlanId === 2);
        if (weekendExtraHour) {
          weekendRow['lor_Per Hour'] = weekendExtraHour.rate?.toString() || '';
          weekendRow.rateExtraHourIds[2] = weekendExtraHour.id;
          weekendRow.blackoutInfo['lor_Per Hour'] = weekendExtraHour.isBlackout;
        }
        
        // Populate Weekend Per Day 1-6 from rateLorAdjustmentDTOS (fkLorId 7-12)
        [1, 2, 3, 4, 5, 6].forEach(i => {
          const lorRate = cat.rateLorAdjustmentDTOS?.find(r => r.fkLorId === 6 + i);
          if (lorRate) {
            weekendRow[`lor_Per Day ${i}`] = lorRate.rate?.toString() || '';
            weekendRow.rateLorIds[6 + i] = lorRate.id;
            weekendRow.blackoutInfo[`lor_Per Day ${i}`] = lorRate.isBlackout;
          }
        });
        
        newWeekdaysData[cat.id] = weekdayRow;
        newWeekendData[cat.id] = weekendRow;
        newMonthlyData[cat.id] = monthlyRow;
      });
      
      setAllWeekdaysData(newWeekdaysData);
      setAllWeekendData(newWeekendData);
      setAllMonthlyData(newMonthlyData);
    }
  }, [isUpdate, isSearchSuccess, searchData, vehicleCategoryDD, setValue]);

  useEffect(() => {
    if (isUpdate && isSearchSuccess && searchData?.categories && fetchRevenuePreview) {
      const firstCategory = searchData.categories[0];
      if (firstCategory && firstCategory.id) {
        const dailyBase = firstCategory.rateBaseDTO?.find(r => r.fkRatePlanId === 1);
        const weekendBase = firstCategory.rateBaseDTO?.find(r => r.fkRatePlanId === 2);
        const weeklyBase = firstCategory.rateBaseDTO?.find(r => r.fkRatePlanId === 3);
        const monthlyBase = firstCategory.rateBaseDTO?.find(r => r.fkRatePlanId === 4);
        
        const payload = {
          rateId: firstCategory.id,
          rateBaseDTO: [
            { fkRatePlanId: 1, rate: parseFloat(dailyBase?.rate) || 0, miles: 0 },
            { fkRatePlanId: 2, rate: parseFloat(weekendBase?.rate) || 0, miles: 0 },
            { fkRatePlanId: 3, rate: parseFloat(weeklyBase?.rate) || 0, miles: 0 },
            { fkRatePlanId: 4, rate: parseFloat(monthlyBase?.rate) || 0, miles: 0 }
          ]
        };
        fetchRevenuePreview(payload);
      }
    }
  }, [isUpdate, isSearchSuccess, searchData, fetchRevenuePreview]);

  // Build the categories array in backend format from weekdays/weekend table data
  const buildCategoriesPayload = () => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return [];
    }
    
    return selectedCategories.map(categoryId => {
      const weekdayRow = allWeekdaysData[categoryId] || {};
      const weekendRow = allWeekendData[categoryId] || {};
      const monthlyRow = allMonthlyData[categoryId] || {};
      
      const categoryPayload = {
        fkCategory: categoryId,
        extraMiles: 0,
        rateBaseDTO: [],
        rateExtraHourDTOS: [],
        rateExtraDayDTOS: [],
        rateLorAdjustmentDTOS: []
      };

      // rateBaseDTO - sequence 1, 2, 3, 4
      // fkRatePlanId: 1=Daily (Weekdays Base)
      categoryPayload.rateBaseDTO.push({
        fkRatePlanId: 1,
        miles: 0,
        rate: weekdayRow['lor_Base'] || 0
      });

      // fkRatePlanId: 2=Weekend (Weekend Base)
      // If excludeWeekends is checked, use weekday Base value; otherwise use weekend Base value
      categoryPayload.rateBaseDTO.push({
        fkRatePlanId: 2,
        miles: 0,
        rate: excludeWeekendsState ? (weekdayRow['lor_Base'] || 0) : (weekendRow['lor_Base'] || 0)
      });

      // fkRatePlanId: 3 - Per Week BASE (Weekdays)
      categoryPayload.rateBaseDTO.push({
        fkRatePlanId: 3,
        miles: 0,
        rate: weekdayRow['lor_Per Week BASE'] || 0
      });
      
      // fkRatePlanId: 4=Monthly (Monthly Base)
      categoryPayload.rateBaseDTO.push({
        fkRatePlanId: 4,
        miles: 0,
        rate: monthlyRow['lor_Base'] || 0
      });

      // rateExtraDayDTOS - sequence 1, 2, 3, 4
      // fkRatePlanId 1, 2: rate: 0, miles: 0
      [1, 2].forEach(planId => {
        categoryPayload.rateExtraDayDTOS.push({
          fkRatePlanId: planId,
          rate: 0,
          miles: 0
        });
      });
      
      // fkRatePlanId 3: Per Week Extra Days (Weekdays)
      categoryPayload.rateExtraDayDTOS.push({
        fkRatePlanId: 3,
        rate: weekdayRow['lor_Per Week Extra Days'] || 0,
        miles: 0
      });
      
      // fkRatePlanId 4: Monthly Extra Day value
      categoryPayload.rateExtraDayDTOS.push({
        fkRatePlanId: 4,
        rate: monthlyRow['lor_Extra Day'] || 0,
        miles: 0
      });

      // All plans share the same extra hour rate (driven by weekday Per Hour)
      if (weekdayRow['lor_Per Hour']) {
        [1, 2, 3, 4].forEach(planId => {
          categoryPayload.rateExtraHourDTOS.push({
            fkRatePlanId: planId,
            rate: weekdayRow['lor_Per Hour'] || "0"
          });
        });
      }

      // Per Day LOR Adjustments (1-6) for Weekdays - fkLorId 1-6
      [1, 2, 3, 4, 5, 6].forEach(i => {
        categoryPayload.rateLorAdjustmentDTOS.push({
          fkLorId: i,
          rate: weekdayRow[`lor_Per Day ${i}`] || 0
        });
      });

      // Weekend LOR Adjustments - Per Day 1-6 for Weekend uses fkLorId 7-12
      // If excludeWeekends is checked, use weekday Per Day values; otherwise use weekend Per Day values
      [1, 2, 3, 4, 5, 6].forEach(i => {
        categoryPayload.rateLorAdjustmentDTOS.push({
          fkLorId: 6 + i, // Weekend Per Day IDs: 7, 8, 9, 10, 11, 12
          rate: excludeWeekendsState ? (weekdayRow[`lor_Per Day ${i}`] || 0) : (weekendRow[`lor_Per Day ${i}`] || 0)
        });
      });

      // Per Week LOR Adjustments (1ST-4TH) for Weekdays - fkLorId 13-16
      const weekLabels = ['1ST', '2ND', '3RD', '4TH'];
      weekLabels.forEach((label, idx) => {
        categoryPayload.rateLorAdjustmentDTOS.push({
          fkLorId: 13 + idx, // Per Week IDs: 13, 14, 15, 16
          rate: weekdayRow[`lor_Per Week ${label}`] || 0
        });
      });

      return categoryPayload;
    });
  };

  // Handle Search button click (for Update mode)
  const handleSearch = () => {
    const locationId = watch("fkLocationId");
    const categoryIds = watch("fkCategory");
    const startDate = watch("startDate");
    const endDate = watch("endDate");

    // Validate required fields
    if (!locationId || !categoryIds || categoryIds.length === 0 || !startDate || !endDate) {
      // You can add toast notification here for validation error
      return;
    }

    // Reset previous data before new search
    setAllWeekdaysData({});
    setAllWeekendData({});
    setAllMonthlyData({});
    setHasSearched(true);

    const payload = {
      locationId: locationId,
      categoryIds: categoryIds,
      startDate: formatDatesHyphan(startDate),
      endDate: formatDatesHyphan(endDate),
    };

    searchAdvanceRate(payload);
  };

  // Check if rate data is available after search
  const hasRateData = isSearchSuccess && searchData?.categories?.length > 0;

  // Determine if tables should be shown
  // For Create mode (!isUpdate): always show tables by default
  // For Update mode (isUpdate): only show after successful search with data
  const shouldShowTables = isUpdate 
    ? (hasSearched && hasRateData) 
    : true;

  // Determine if "no data" message should be shown (only in Update mode)
  const shouldShowNoDataMessage = isUpdate && hasSearched && !isSearchLoading && !hasRateData;

  // Check if any category has isRateMirror: true (rates cannot be edited)
  const hasRateMirror = isUpdate && isSearchSuccess && searchData?.categories?.some(cat => cat.isRateMirror === true);

  // Get the rateMirrorFrom info from the first mirrored category (for navigation link)
  const rateMirrorInfo = isUpdate && isSearchSuccess && searchData?.categories?.find(cat => cat.isRateMirror === true)?.rateMirrorFrom;

  // Build the categories array for update payload (includes rateId)
  const buildUpdateCategoriesPayload = () => {
    const weekdayRows = Object.values(allWeekdaysData);
    if (weekdayRows.length === 0) {
      return [];
    }
    
    return weekdayRows.map(weekdayRow => {
      const rateRecordId = weekdayRow.id;
      const weekendRow = allWeekendData[rateRecordId] || {};
      const monthlyRow = allMonthlyData[rateRecordId] || {};
      
      const categoryPayload = {
        id: weekdayRow.rateId || null,
        fkCategory: weekdayRow.fkCategory,
        extraMiles: 0,
        startDate: weekdayRow.startDate || null,
        endDate: weekdayRow.endDate || null,
        rateBaseDTO: [],
        rateExtraHourDTOS: [],
        rateExtraDayDTOS: [],
        rateLorAdjustmentDTOS: []
      };

      // rateBaseDTO - sequence 1, 2, 3, 4
      // fkRatePlanId: 1=Daily (Weekdays Base)
      const baseDto1 = {
        fkRatePlanId: 1,
        miles: 0,
        rate: weekdayRow['lor_Base'] || 0
      };
      if (weekdayRow.rateBaseIds?.[1]) baseDto1.id = weekdayRow.rateBaseIds[1];
      categoryPayload.rateBaseDTO.push(baseDto1);

      // fkRatePlanId: 2=Weekend (Weekend Base)
      // If excludeWeekends is checked, use weekday Base value; otherwise use weekend Base value
      const baseDto2 = {
        fkRatePlanId: 2,
        miles: 0,
        rate: excludeWeekendsState ? (weekdayRow['lor_Base'] || 0) : (weekendRow['lor_Base'] || 0)
      };
      if (weekendRow.rateBaseIds?.[2]) baseDto2.id = weekendRow.rateBaseIds[2];
      categoryPayload.rateBaseDTO.push(baseDto2);

      // fkRatePlanId: 3 - Per Week BASE (Weekdays)
      const baseDto3 = {
        fkRatePlanId: 3,
        miles: 0,
        rate: weekdayRow['lor_Per Week BASE'] || 0
      };
      if (weekdayRow.rateBaseIds?.[3]) baseDto3.id = weekdayRow.rateBaseIds[3];
      categoryPayload.rateBaseDTO.push(baseDto3);
      
      // fkRatePlanId: 4=Monthly (Monthly Base)
      const baseDto4 = {
        fkRatePlanId: 4,
        miles: 0,
        rate: monthlyRow['lor_Base'] || 0
      };
      if (monthlyRow.rateBaseIds?.[4]) baseDto4.id = monthlyRow.rateBaseIds[4];
      categoryPayload.rateBaseDTO.push(baseDto4);

      // rateExtraDayDTOS - sequence 1, 2, 3, 4
      // fkRatePlanId 1, 2: rate: 0, miles: 0
      [1, 2].forEach(planId => {
        const extraDayDto = {
          fkRatePlanId: planId,
          rate: 0,
          miles: 0
        };
        if (weekdayRow.rateExtraDayIds?.[planId]) extraDayDto.id = weekdayRow.rateExtraDayIds[planId];
        categoryPayload.rateExtraDayDTOS.push(extraDayDto);
      });
      
      // fkRatePlanId 3: Per Week Extra Days (Weekdays)
      const extraDayDto3 = {
        fkRatePlanId: 3,
        rate: weekdayRow['lor_Per Week Extra Days'] || 0,
        miles: 0
      };
      if (weekdayRow.rateExtraDayIds?.[3]) extraDayDto3.id = weekdayRow.rateExtraDayIds[3];
      categoryPayload.rateExtraDayDTOS.push(extraDayDto3);
      
      // fkRatePlanId 4: Monthly Extra Day value
      const extraDayDto4 = {
        fkRatePlanId: 4,
        rate: monthlyRow['lor_Extra Day'] || 0,
        miles: 0
      };
      if (monthlyRow.rateExtraDayIds?.[4]) extraDayDto4.id = monthlyRow.rateExtraDayIds[4];
      categoryPayload.rateExtraDayDTOS.push(extraDayDto4);

      // All plans share the same extra hour rate (driven by weekday Per Hour)
      // IDs: plan 1/3/4 stored in weekdayRow, plan 2 stored in weekendRow
      if (weekdayRow['lor_Per Hour']) {
        [1, 2, 3, 4].forEach(planId => {
          const hourlyDto = {
            fkRatePlanId: planId,
            rate: weekdayRow['lor_Per Hour'] || "0"
          };
          const idSource = planId === 2 ? weekendRow : weekdayRow;
          if (idSource.rateExtraHourIds?.[planId]) hourlyDto.id = idSource.rateExtraHourIds[planId];
          categoryPayload.rateExtraHourDTOS.push(hourlyDto);
        });
      }

      // Per Day LOR adjustments for weekdays (1-6) - fkLorId 1-6
      [1, 2, 3, 4, 5, 6].forEach(i => {
        const lorDto = {
          fkLorId: i,
          rate: weekdayRow[`lor_Per Day ${i}`] || 0
        };
        if (weekdayRow.rateLorIds?.[i]) lorDto.id = weekdayRow.rateLorIds[i];
        categoryPayload.rateLorAdjustmentDTOS.push(lorDto);
      });

      // Weekend Per Day LOR adjustments (1-6) - fkLorId 7-12
      // If excludeWeekends is checked, use weekday Per Day values; otherwise use weekend Per Day values
      [1, 2, 3, 4, 5, 6].forEach(i => {
        const weekendLorDto = {
          fkLorId: 6 + i, // Weekend Per Day IDs: 7, 8, 9, 10, 11, 12
          rate: excludeWeekendsState ? (weekdayRow[`lor_Per Day ${i}`] || 0) : (weekendRow[`lor_Per Day ${i}`] || 0)
        };
        if (weekendRow.rateLorIds?.[6 + i]) weekendLorDto.id = weekendRow.rateLorIds[6 + i];
        categoryPayload.rateLorAdjustmentDTOS.push(weekendLorDto);
      });

      // Per Week LOR adjustments for weekdays (1ST-4TH) - fkLorId 13-16
      const weekLabels = ['1ST', '2ND', '3RD', '4TH'];
      weekLabels.forEach((label, idx) => {
        const lorDto = {
          fkLorId: 13 + idx, // Per Week IDs: 13, 14, 15, 16
          rate: weekdayRow[`lor_Per Week ${label}`] || 0
        };
        if (weekdayRow.rateLorIds?.[13 + idx]) lorDto.id = weekdayRow.rateLorIds[13 + idx];
        categoryPayload.rateLorAdjustmentDTOS.push(lorDto);
      });

      return categoryPayload;
    });
  };

  const onSubmit = (data) => {
    if (isUpdate) {
      // Update payload for updateAdvancedRate API
      const updatePayload = {
        userId: localStorage.getItem("userId"),
        fkLocationId: data.fkLocationId,
        fkRateGroup: data.fkRateGroup,
        fkNetworkId: data.fkNetworkId,
        fkRateTypeId: data.fkRateTypeId,
        rateOption: data.rateOption,
        status: 1,
        categories: buildUpdateCategoriesPayload()
      };
      updateAdvanceRate(updatePayload);
    } else {
      const payload = {
        userId: localStorage.getItem("userId"),
        fkLocationId: data.fkLocationId,
        startDate: formatDatesHyphan(data.startDate),
        endDate: formatDatesHyphan(data.endDate),
        fkRateGroup: data.fkRateGroup,
        fkNetworkId: data.fkNetworkId,
        fkRateTypeId: data.fkRateTypeId,
        rateOption: data.rateOption,
        status: 1,
        categories: buildCategoriesPayload()
      };

      isEdit
        ? editRateAdminAdvanceRate({ id: data.id, data: payload })
        : createRateAdminAdvanceRate(payload);
    }
  };

  const getToastProps = () => {
    const toastTitle = "Please select category from list";
    return {
      severity: TOAST_SEVERITY.ERROR,
      toastTitle,
      shouldShowToast: false,
    };
  };

  const getFormErrorMessage = (name) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const renderPageHeaderSection = () => {
    return (
      <div className="make-relative sub-header ">
        <div className="maintitlediv">
          <div className="ml-3">
            <h4 className="pagetitle">
              {isEdit || isUpdate ? "Advance Rate" : "Create Advance Rate"}
            </h4>
          </div>
          <div className="buttonarea">
            {screenPermissions?.create && isUpdate && (
              <Button
                label={"Create Advance Rate"}
                className={`p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={handleCreateRateAdminAdvanceRate}
              />
            )}
            {!isEdit && !isUpdate && (
              <Button
                tooltip={"Back to Advance Rate"}
                tooltipOptions={{ position: "left" }}
                label={"Back to Advance Rate"}
                className={`p-button `}
                style={{ borderRadius: "10px" }}
                onClick={navigateToRateAdminAdvanceRateScreen}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderWeekdaysInputCell = (rowData, lor, placeholder = "") => {
    const isPerDayField = lor.startsWith('Per Day');
    const isPerHourField = lor === 'Per Hour';
    const isPerWeekLorField = ['Per Week 1ST', 'Per Week 2ND', 'Per Week 3RD', 'Per Week 4TH'].includes(lor);
    const baseRate = rowData['lor_Base'] || 0;
    const perWeekBaseRate = rowData['lor_Per Week BASE'] || 0;
    const currentValue = rowData[`lor_${lor}`] || '';
    
    // Check if field has blackout
    const fieldKey = `lor_${lor}`;
    const isBlackout = rowData.blackoutInfo?.[fieldKey] === true;
    
    // Get the field ID to check if it was recently changed
    let fieldId = null;
    if (lor === 'Base') fieldId = rowData.rateBaseIds?.[1];
    else if (lor === 'Per Hour') fieldId = rowData.rateExtraHourIds?.[1];
    else if (lor === 'Per Week BASE') fieldId = rowData.rateBaseIds?.[3];
    else if (lor === 'Per Week Extra Days') fieldId = rowData.rateExtraDayIds?.[3];
    else if (lor.startsWith('Per Day ')) {
      const dayNum = parseInt(lor.replace('Per Day ', ''));
      fieldId = rowData.rateLorIds?.[dayNum];
    } else if (lor.startsWith('Per Week ')) {
      const weekLabels = { '1ST': 13, '2ND': 14, '3RD': 15, '4TH': 16 };
      const label = lor.replace('Per Week ', '');
      fieldId = rowData.rateLorIds?.[weekLabels[label]];
    }
    
    const isRecentlyChanged = fieldId && rowData.changedFieldIds?.includes(fieldId);
    
    // Determine CSS class: green for changed, gray for blackout only
    const getInputClass = () => {
      if (isRecentlyChanged) return 'matrix-input changed-field-input';
      if (isBlackout) return 'matrix-input blackout-field-input';
      return 'matrix-input';
    };
    
    // Determine if input should be disabled (blackout or rate mirror)
    const isDisabled = isBlackout || hasRateMirror;
    
    const inputElement = (
      <div className={isRecentlyChanged ? 'changed-field-wrapper' : (isBlackout ? 'blackout-field-wrapper' : (hasRateMirror ? 'rate-mirror-field-wrapper' : ''))}>
        <input
          type="text"
          className={getInputClass()}
          value={currentValue}
          placeholder={placeholder}
          disabled={isDisabled}
          onChange={(e) => updateWeekdaysData(rowData.id, lor, e.target.value)}
        />
        {isPerHourField && (
          <span className="red" style={{ fontSize: '11px', display: 'block', color: 'red' }}>
            Effective
          </span>
        )}
        {isPerDayField && (
          <span className="red" style={{ fontSize: '11px', display: 'block', color: 'red' }}>
            {additionbaselor(baseRate, currentValue || 0)}
          </span>
        )}
        {isPerWeekLorField && (
          <span className="red" style={{ fontSize: '11px', display: 'block', color: 'red' }}>
            {additionbaselor(perWeekBaseRate, currentValue || 0)}
          </span>
        )}
      </div>
    );
    
    // Priority: BlackoutTooltip > RateMirrorTooltip
    // Wrap with tooltip if blackout
    if (isBlackout) {
      return (
        <BlackoutTooltip 
          fieldName={`weekday-${rowData.id}-${lor}`} 
          isBlackout={isBlackout} 
          blackoutDates={rowData.blackoutDates}
          rateId={rowData.id}
        >
          {inputElement}
        </BlackoutTooltip>
      );
    }
    
    // Wrap with RateMirrorTooltip if rate is mirrored (and not blacked out)
    if (rowData.isRateMirror && rowData.rateMirrorFrom) {
      return (
        <RateMirrorTooltip 
          fieldName={`weekday-${rowData.id}-${lor}`} 
          isRateMirror={rowData.isRateMirror}
          rateMirrorFrom={rowData.rateMirrorFrom}
          rateId={rowData.id}
          isBlackout={isBlackout}
        >
          {inputElement}
        </RateMirrorTooltip>
      );
    }
    
    // Wrap with RevenuePreviewTooltip for Per Day fields (not blacked out, not mirrored)
    if (isPerDayField && revenuePreviewData) {
      const dayNum = parseInt(lor.replace('Per Day ', ''));
      const lorId = dayNum; // Weekdays Per Day uses fkLorId 1-6
      return (
        <RevenuePreviewTooltip
          lorId={lorId}
          revenuePreviewData={revenuePreviewData}
          isLoading={isLoading}
          rateId={rowData.id}
          isBlackout={isBlackout}
          isRateMirror={rowData.isRateMirror}
        >
          {inputElement}
        </RevenuePreviewTooltip>
      );
    }
    
    // Wrap with RevenuePreviewTooltip for Per Week LOR fields (not blacked out, not mirrored)
    if (isPerWeekLorField && revenuePreviewData) {
      const weekLabels = { '1ST': 13, '2ND': 14, '3RD': 15, '4TH': 16 };
      const label = lor.replace('Per Week ', '');
      const lorId = weekLabels[label];
      if (lorId) {
        return (
          <RevenuePreviewTooltip
            lorId={lorId}
            revenuePreviewData={revenuePreviewData}
            isLoading={isLoading}
            rateId={rowData.id}
            isBlackout={isBlackout}
            isRateMirror={rowData.isRateMirror}
          >
            {inputElement}
          </RevenuePreviewTooltip>
        );
      }
    }
    
    return inputElement;
  };

  const renderWeekendInputCell = (rowData, lor, placeholder = "") => {
    const isPerDayField = lor.startsWith('Per Day');
    const baseRate = rowData['lor_Base'] || 0;
    const currentValue = rowData[`lor_${lor}`] || '';
    
    // Check if field has blackout
    const fieldKey = `lor_${lor}`;
    const isBlackout = rowData.blackoutInfo?.[fieldKey] === true;
    
    // Get the field ID to check if it was recently changed
    let fieldId = null;
    if (lor === 'Base') fieldId = rowData.rateBaseIds?.[2];
    else if (lor === 'Per Hour') fieldId = rowData.rateExtraHourIds?.[2];
    else if (lor.startsWith('Per Day ')) {
      const dayNum = parseInt(lor.replace('Per Day ', ''));
      fieldId = rowData.rateLorIds?.[6 + dayNum]; // Weekend Per Day uses fkLorId 7-12
    }
    
    const isRecentlyChanged = fieldId && rowData.changedFieldIds?.includes(fieldId);
    
    // Determine CSS class: green for changed, gray for blackout only
    const getInputClass = () => {
      if (isRecentlyChanged) return 'matrix-input changed-field-input';
      if (isBlackout) return 'matrix-input blackout-field-input';
      return 'matrix-input';
    };
    
    // Determine if input should be disabled (blackout or rate mirror)
    const isDisabled = isBlackout || hasRateMirror;
    
    const inputElement = (
      <div className={isRecentlyChanged ? 'changed-field-wrapper' : (isBlackout ? 'blackout-field-wrapper' : (hasRateMirror ? 'rate-mirror-field-wrapper' : ''))}>
        <input
          type="text"
          className={getInputClass()}
          value={currentValue}
          placeholder={placeholder}
          disabled={isDisabled}
          onChange={(e) => updateWeekendData(rowData.id, lor, e.target.value)}
        />
        {isPerDayField && (
          <span className="red" style={{ fontSize: '11px', display: 'block', color: 'red' }}>
            {additionbaselor(baseRate, currentValue || 0)}
          </span>
        )}
      </div>
    );
    
    // Priority: BlackoutTooltip > RateMirrorTooltip
    // Wrap with tooltip if blackout
    if (isBlackout) {
      return (
        <BlackoutTooltip 
          fieldName={`weekend-${rowData.id}-${lor}`} 
          isBlackout={isBlackout} 
          blackoutDates={rowData.blackoutDates}
          rateId={rowData.id}
        >
          {inputElement}
        </BlackoutTooltip>
      );
    }
    
    // Wrap with RateMirrorTooltip if rate is mirrored (and not blacked out)
    if (rowData.isRateMirror && rowData.rateMirrorFrom) {
      return (
        <RateMirrorTooltip 
          fieldName={`weekend-${rowData.id}-${lor}`} 
          isRateMirror={rowData.isRateMirror}
          rateMirrorFrom={rowData.rateMirrorFrom}
          isBlackout={isBlackout}
          rateId={rowData.id}
        >
          {inputElement}
        </RateMirrorTooltip>
      );
    }
    
    // Wrap with RevenuePreviewTooltip for Per Day fields (not blacked out, not mirrored)
    if (isPerDayField && revenuePreviewData) {
      const dayNum = parseInt(lor.replace('Per Day ', ''));
      const lorId = 6 + dayNum; // Weekend Per Day uses fkLorId 7-12
      return (
        <RevenuePreviewTooltip
          lorId={lorId}
          revenuePreviewData={revenuePreviewData}
          isLoading={isLoading}
          rateId={rowData.id}
          isBlackout={isBlackout}
          isRateMirror={rowData.isRateMirror}
        >
          {inputElement}
        </RevenuePreviewTooltip>
      );
    }
    
    return inputElement;
  };

  const renderMonthlyInputCell = (rowData, lor, placeholder = "") => {
    // Get monthly data from allMonthlyData using category id
    const monthlyRow = allMonthlyData[rowData.id] || {};
    const currentValue = monthlyRow[`lor_${lor}`] || '';
    
    // Check if field has blackout
    const fieldKey = `lor_${lor}`;
    const isBlackout = monthlyRow.blackoutInfo?.[fieldKey] === true;
    
    // Get the field ID to check if it was recently changed
    let fieldId = null;
    if (lor === 'Base') fieldId = monthlyRow.rateBaseIds?.[4];
    else if (lor === 'Extra Day') fieldId = monthlyRow.rateExtraDayIds?.[4];
    
    const isRecentlyChanged = fieldId && monthlyRow.changedFieldIds?.includes(fieldId);
    
    // Determine CSS class: green for changed, gray for blackout only
    const getInputClass = () => {
      if (isRecentlyChanged) return 'matrix-input changed-field-input';
      if (isBlackout) return 'matrix-input blackout-field-input';
      return 'matrix-input';
    };
    
    // Determine if input should be disabled (blackout or rate mirror)
    const isDisabled = isBlackout || hasRateMirror;
    
    const inputElement = (
      <div className={isRecentlyChanged ? 'changed-field-wrapper' : (isBlackout ? 'blackout-field-wrapper' : (hasRateMirror ? 'rate-mirror-field-wrapper' : ''))}>
        <input
          type="text"
          className={getInputClass()}
          value={currentValue}
          placeholder={placeholder}
          disabled={isDisabled}
          onChange={(e) => updateMonthlyData(rowData.id, lor, e.target.value)}
        />
      </div>
    );
    
    // Priority: BlackoutTooltip > RateMirrorTooltip
    // Wrap with tooltip if blackout
    if (isBlackout) {
      return (
        <BlackoutTooltip 
          fieldName={`monthly-${rowData.id}-${lor}`} 
          isBlackout={isBlackout} 
          blackoutDates={monthlyRow.blackoutDates}
          rateId={rowData.id}
        >
          {inputElement}
        </BlackoutTooltip>
      );
    }
    
    // Wrap with RateMirrorTooltip if rate is mirrored (and not blacked out)
    if (monthlyRow.isRateMirror && monthlyRow.rateMirrorFrom) {
      return (
        <RateMirrorTooltip 
          fieldName={`monthly-${rowData.id}-${lor}`} 
          isRateMirror={monthlyRow.isRateMirror}
          rateMirrorFrom={monthlyRow.rateMirrorFrom}
          isBlackout={isBlackout}
          rateId={rowData.id}
        >
          {inputElement}
        </RateMirrorTooltip>
      );
    }
    
    return inputElement;
  };

  return (
    <Container maxWidth="xl">
      <Breadcrumb list={RateAdminAdvanceRateBreadcrumb} />
      <EzToast {...getToastProps()} />
      <div className={`containerbox ${classes.bg}`}>
        {renderPageHeaderSection()}
        <div className="form-demo">
          <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
            {/* Row 1: Location, Category, From, To */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <EzControlledDropDowntWithFloatingLabel
                  name="fkLocationId"
                  control={control}
                  rules={{ required: "Location is required" }}
                  optionLabel="name"
                  optionValue="id"
                  options={locationDD || []}
                  isError={errors.fkLocationId}
                  errorMsg={getFormErrorMessage("fkLocationId")}
                  label="Location"
                  labelClassName="ml-2"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EzControlledMultiSelecttWithFloatingLabel
                  name="fkCategory"
                  control={control}
                  rules={{ required: "Category is required" }}
                  optionLabel="name"
                  optionValue="id"
                  options={locationCategoryDD || []}
                  isError={errors.fkCategory}
                  errorMsg={getFormErrorMessage("fkCategory")}
                  label="Category"
                  labelClassName="ml-2"
                  disabled={!isEdit && !isUpdate && (!selectedLocationId || isLocationCategoryDDLoading)}
                  placeholder={!selectedLocationId ? "Select location first" : isLocationCategoryDDLoading ? "Loading..." : "Select Category"}
                  emptyMessage={!selectedLocationId ? "Please select a location first" : "No categories available for this location"}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EzControlledCalendarWithFloatingLabel
                  id="startDate"
                  name="startDate"
                  showTime={false}
                  minDate={!isEdit && !isUpdate ? new Date() : undefined}
                  maxDate={new Date(2090, 0, 1)}
                  dateFormat="yy/mm/dd"
                  control={control}
                  isError={errors.startDate}
                  errorMsg={getFormErrorMessage("startDate")}
                  label="From"
                  rules={{ required: "From date is required" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EzControlledCalendarWithFloatingLabel
                  id="endDate"
                  name="endDate"
                  showTime={false}
                  minDate={!isEdit && !isUpdate ? new Date() : undefined}
                  maxDate={new Date(2090, 0, 1)}
                  dateFormat="yy/mm/dd"
                  control={control}
                  isError={errors.endDate}
                  errorMsg={getFormErrorMessage("endDate")}
                  label="To"
                  rules={{ required: "To date is required" }}
                />
              </Grid>
            </Grid>

            {/* Row 2: Rate Group, Networks, Rate Option, Rate Type (Disabled dropdowns) */}
            <Grid container spacing={2} style={{ marginTop: '10px' }}>
              <Grid item xs={12} sm={6} md={3}>
                <EzControlledDropDowntWithFloatingLabel
                  name="fkRateGroup"
                  control={control}
                  optionLabel="name"
                  optionValue="id"
                  options={RateCodeDD || []}
                  label="Rate Group"
                  labelClassName="ml-2"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EzControlledDropDowntWithFloatingLabel
                  name="fkNetworkId"
                  control={control}
                  optionLabel="name"
                  optionValue="id"
                  options={RateNetworkDD || []}
                  label="Networks"
                  labelClassName="ml-2"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EzControlledDropDowntWithFloatingLabel
                  name="rateOption"
                  control={control}
                  optionLabel="name"
                  optionValue="id"
                  options={RateOptionsDD || []}
                  label="Rate Option"
                  labelClassName="ml-2"
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EzControlledDropDowntWithFloatingLabel
                  name="fkRateTypeId"
                  control={control}
                  optionLabel="name"
                  optionValue="id"
                  options={rateTypeDD || []}
                  label="Rate Type"
                  labelClassName="ml-2"
                  disabled={true}
                />
              </Grid>
            </Grid>

            {/* Search Button Row - Only for Update mode */}
            {isUpdate && (
              <Grid container spacing={2} style={{justifyContent: 'flex-end' }}>
                <Grid item>
                  <Button
                    type="button"
                    label={isSearchLoading ? "Searching..." : "Search"}
                    icon="pi pi-search"
                    className="p-button-primary p-button-sm"
                    onClick={handleSearch}
                    disabled={isSearchLoading}
                    style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Exclude Weekends Checkbox - Only show when tables should be visible */}
            {shouldShowTables && (
              <Grid container spacing={2} style={{ marginTop: "10px" }}>
                <Grid item xs={12}>
                  <EzControlledCheckBoxWithLabel
                    name="excludeWeekends"
                    control={control}
                    label="Exclude Weekends"
                    value={excludeWeekendsState}
                    onChange={(e) => setExcludeWeekendsState(e.checked)}
                  />
                </Grid>
              </Grid>
            )}

            {/* Loading indicator for search */}
            {isUpdate && isSearchLoading && (
              <Grid container spacing={2} style={{ marginTop: "20px" }}>
                <Grid item xs={12}>
                  <div style={{ padding: '40px', textAlign: 'center', color: '#666', border: '1px dashed #ccc', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', marginBottom: '10px' }}></i>
                    <div>Searching for rates...</div>
                  </div>
                </Grid>
              </Grid>
            )}

            {/* No Data Message for Update mode */}
            {shouldShowNoDataMessage && (
              <Grid container spacing={2} style={{ marginTop: "20px" }}>
                <Grid item xs={12}>
                  <div style={{ 
                    padding: '40px', 
                    textAlign: 'center', 
                    color: '#d32f2f', 
                    border: '1px dashed #d32f2f', 
                    borderRadius: '4px', 
                    backgroundColor: '#ffebee' 
                  }}>
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: '10px', display: 'block' }}></i>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>
                      Rate is not available for the selected location, category, and date range.
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      Please try with different search criteria.
                    </div>
                  </div>
                </Grid>
              </Grid>
            )}

            {/* Prompt message for Update mode before search */}
            {isUpdate && !hasSearched && (
              <Grid container spacing={2} style={{ marginTop: "20px" }}>
                <Grid item xs={12}>
                  <div style={{ 
                    padding: '40px', 
                    textAlign: 'center', 
                    color: '#1976d2', 
                    border: '1px dashed #1976d2', 
                    borderRadius: '4px', 
                    backgroundColor: '#e3f2fd' 
                  }}>
                    <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '10px', display: 'block' }}></i>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>
                      Select Location, Category, and Date Range then click "Search" to fetch rates.
                    </div>
                  </div>
                </Grid>
              </Grid>
            )}

            {/* Rate Mirror Warning Message */}
            {hasRateMirror && (
              <Grid container spacing={2} style={{ marginTop: "20px" }}>
                <Grid item xs={12}>
                  <div style={{ 
                    padding: '15px 20px', 
                    textAlign: 'left', 
                    color: '#ed6c02', 
                    border: '1px solid #ed6c02', 
                    borderRadius: '4px', 
                    backgroundColor: '#fff4e5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <i className="pi pi-lock" style={{ fontSize: '1.5rem' }}></i>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>
                        This rate is mirrored and cannot be edited.
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        To make changes, please update the source rate from{' '}
                        <span 
                          style={{ color: '#ed6c02', textDecoration: 'underline', cursor: 'pointer', fontWeight: '500' }}
                          onClick={() => rateMirrorInfo?.id && navigate(`/create-rate-mirrors/${rateMirrorInfo.id}/edit`)}
                        >
                          Rate Mirrors
                        </span>.
                      </div>
                    </div>
                  </div>
                </Grid>
              </Grid>
            )}

            {/* ===================== WEEKDAYS TABLE ===================== */}
            {shouldShowTables && (
            <Grid container spacing={2} style={{ marginTop: "20px" }}>
              <Grid item xs={12}>
                <div className="rate-matrix-wrapper">
                  <div className="rate-matrix-header matrix-shifted-header">
                    <span style={{ fontWeight: '600' }}>WEEKDAYS</span>
                  </div>

                  {weekdaysData.length > 0 ? (
                    <DataTable
                      value={weekdaysData}
                      className="rate-matrix-table grouped-header-table"
                      responsiveLayout="scroll"
                      headerColumnGroup={
                        <ColumnGroup>
                          <Row>
                            <Column header="" rowSpan={2} style={{ width: "80px" }} />
                            <Column header="Base" rowSpan={2} style={{ width: "80px", textAlign: 'center' }} />
                            <Column header="Per Hour" rowSpan={2} style={{ width: "80px", textAlign: 'center' }} />
                            <Column header={<div style={{ textAlign: 'center', width: '100%' }}>Per Day</div>} colSpan={6} />
                            <Column header={<div style={{ textAlign: 'center', width: '100%' }}>Per Week</div>} colSpan={6} />
                            <Column header={<div style={{ textAlign: 'center', width: '100%' }}>Monthly</div>} colSpan={2} />
                          </Row>
                          <Row>
                            <Column header="1" style={{ width: "50px" }} />
                            <Column header="2" style={{ width: "50px" }} />
                            <Column header="3" style={{ width: "50px" }} />
                            <Column header="4" style={{ width: "50px" }} />
                            <Column header="5" style={{ width: "50px" }} />
                            <Column header="6" style={{ width: "50px" }} />
                            <Column header="BASE" style={{ width: "55px" }} />
                            <Column header="Extra Days" style={{ width: "70px" }} />
                            <Column header="1ST" style={{ width: "55px" }} />
                            <Column header="2ND" style={{ width: "55px" }} />
                            <Column header="3RD" style={{ width: "55px" }} />
                            <Column header="4TH" style={{ width: "55px" }} />
                            <Column header="Base" style={{ width: "70px" }} />
                            <Column header="Extra Day" style={{ width: "70px" }} />
                          </Row>
                        </ColumnGroup>
                      }
                    >
                      <Column
                        field="category"
                        body={(rowData) => (
                          <div className="matrix-category" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2px 0' }}>
                            <div style={{ fontWeight: '700', fontSize: '12px', color: '#333', lineHeight: '1.2' }}>{rowData.category}</div>
                            {rowData.startDate && rowData.endDate && (
                              <div style={{ fontSize: '10px', color: '#333', marginTop: '2px', whiteSpace: 'nowrap', lineHeight: '1.3', textAlign: 'center' }}>
                                <div>{rowData.startDate} -</div>
                                <div>{rowData.endDate}</div>
                              </div>
                            )}
                          </div>
                        )}
                      />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Base')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Hour')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Day 1')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Day 2')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Day 3')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Day 4')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Day 5')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Day 6')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Week BASE')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Week Extra Days')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Week 1ST')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Week 2ND')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Week 3RD')} />
                      <Column body={(rowData) => renderWeekdaysInputCell(rowData, 'Per Week 4TH')} />
                      <Column body={(rowData) => renderMonthlyInputCell(rowData, 'Base')} />
                      <Column body={(rowData) => renderMonthlyInputCell(rowData, 'Extra Day')} />
                    </DataTable>
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#888', border: '1px dashed #ccc', borderRadius: '4px' }}>
                      Please select categories to display rates
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
            )}

            {/* ===================== WEEKEND TABLE ===================== */}
            {shouldShowTables && !excludeWeekendsState && (
              <Grid container spacing={2} style={{ marginTop: "20px" }}>
                <Grid item xs={12}>
                  <div className="rate-matrix-wrapper">
                    <div className="rate-matrix-header matrix-shifted-header">
                      <span style={{ fontWeight: '600' }}>WEEKEND</span>
                    </div>

                    {weekendData.length > 0 ? (
                      <DataTable
                        value={weekendData}
                        className="rate-matrix-table grouped-header-table"
                        responsiveLayout="scroll"
                        headerColumnGroup={
                          <ColumnGroup>
                            <Row>
                              <Column header="" rowSpan={2} style={{ width: "80px" }} />
                              <Column header="Base" rowSpan={2} style={{ width: "80px", textAlign: 'center' }} />
                              <Column header={<div style={{ textAlign: 'center', width: '100%' }}>Per Day</div>} colSpan={6} />
                            </Row>
                            <Row>
                              <Column header="1" style={{ width: "50px" }} />
                              <Column header="2" style={{ width: "50px" }} />
                              <Column header="3" style={{ width: "50px" }} />
                              <Column header="4" style={{ width: "50px" }} />
                              <Column header="5" style={{ width: "50px" }} />
                              <Column header="6" style={{ width: "50px" }} />
                            </Row>
                          </ColumnGroup>
                        }
                      >
                        <Column
                          field="category"
                          body={(rowData) => (
                            <div className="matrix-category" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                              <div style={{ fontWeight: '700', fontSize: '12px', color: '#333', lineHeight: '1.2' }}>{rowData.category}</div>
                              {rowData.startDate && rowData.endDate && (
                                <div style={{ fontSize: '10px', color: '#333', whiteSpace: 'nowrap', lineHeight: '1.2', textAlign: 'center' }}>
                                  <div>{rowData.startDate} -</div>
                                  <div>{rowData.endDate}</div>
                                </div>
                              )}
                            </div>
                          )}
                        />
                        <Column body={(rowData) => renderWeekendInputCell(rowData, 'Base')} />
                        <Column body={(rowData) => renderWeekendInputCell(rowData, 'Per Day 1')} />
                        <Column body={(rowData) => renderWeekendInputCell(rowData, 'Per Day 2')} />
                        <Column body={(rowData) => renderWeekendInputCell(rowData, 'Per Day 3')} />
                        <Column body={(rowData) => renderWeekendInputCell(rowData, 'Per Day 4')} />
                        <Column body={(rowData) => renderWeekendInputCell(rowData, 'Per Day 5')} />
                        <Column body={(rowData) => renderWeekendInputCell(rowData, 'Per Day 6')} />
                      </DataTable>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#888', border: '1px dashed #ccc', borderRadius: '4px' }}>
                        Please select categories to display rates
                      </div>
                    )}
                  </div>
                </Grid>
              </Grid>
            )}

            {/* Submit Button - Only show when tables are visible and not rate mirror */}
            {shouldShowTables && !hasRateMirror && (
            <div className="make-flex mt-3 mr-2">
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "end",
                }}
              >
                <React.Fragment>
                  <div
                    style={{ margin: "15px 0px 15px 10px", minWidth: "100px" }}
                  >
                    <Button
                      type="submit"
                      label={isEdit ? "Update" : "Save"}
                      className="mt-2 p-button-raised"
                    />
                  </div>
                </React.Fragment>
              </div>
            </div>
            )}
          </form>
        </div>
      </div>
    </Container>
  );
};

export default CreateRateAdminAdvanceRateComponent;
